import { motion } from 'framer-motion';
import { Wine, Users, HeartHandshake, Glasses, UtensilsCrossed, Disc3, Croissant, Bus } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const SchedulePage = () => {
  const { t } = useLanguage();

  const weddingDays = [
    {
      date: t('friday'),
      subtitle: t('fridaySubtitle'),
      events: [
        {
          time: t('timeWelcome'),
          title: t('welcomeSoiree'),
          icon: Wine,
        },
      ],
    },
    {
      date: t('saturday'),
      subtitle: t('saturdaySubtitle'),
      events: [
        {
          time: t('timeArrival'),
          title: t('guestsArrival'),
          icon: Users,
        },
        {
          time: t('timeCeremony'),
          title: t('ceremony'),
          icon: HeartHandshake,
        },
        {
          time: t('timeCocktail'),
          title: t('cocktailHour'),
          icon: Glasses,
        },
        {
          time: t('timeDinner'),
          title: t('dinner'),
          icon: UtensilsCrossed,
        },
        {
          time: t('timeDance'),
          title: t('soiree'),
          icon: Disc3,
        },
      ],
    },
    {
      date: t('sunday'),
      subtitle: t('sundaySubtitle'),
      events: [
        {
          time: t('timeBrunch'),
          title: t('farewellBrunch'),
          icon: Croissant,
        },
      ],
    },
  ];

  return (
    <section className="pt-14 pb-16 bg-[#FAFAFA]" data-testid="schedule-page">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-4"
        >
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2A26] mb-4">
            {t('scheduleTitle')}
          </h1>
          <div className="w-12 h-0.5 bg-[#B5985A] mx-auto" />
        </motion.div>

        {/* Location Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[#999] text-sm italic mb-16"
        >
          {t('scheduleLocationNote')}
        </motion.p>

        {/* Days */}
        <div className="space-y-16">
          {weddingDays.map((day, dayIndex) => (
            <motion.div
              key={day.date}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + dayIndex * 0.15 }}
            >
              {/* Day Header */}
              <div className="mb-8 pb-4 border-b border-[#E5E5E5]">
                <h2 className="font-serif text-2xl text-[#2D2A26] mb-1">
                  {day.date}
                </h2>
                {day.subtitle && (
                  <p className="text-[#B5985A] text-sm uppercase tracking-[0.15em]">
                    {day.subtitle}
                  </p>
                )}
              </div>

              {/* Arrival note — above Saturday's timeline */}
              {dayIndex === 1 && (
                <p className="text-[#666] leading-relaxed text-center mb-8 max-w-lg mx-auto">{t('scheduleArrivalNote')}</p>
              )}

              {/* Events */}
              <div className="relative max-w-lg mx-auto">
                {/* Continuous timeline line — centered on the icon column */}
                <div className={`absolute left-[calc(7.5rem+1.5rem+1.125rem-1px)] -top-4 w-0.5 bg-[#E5E5E5] ${dayIndex === 1 ? 'bottom-0' : '-bottom-4'}`} />

                <div className="space-y-8">
                  {day.events.map((event, eventIndex) => (
                    <div key={event.title} className="flex gap-6 relative">
                      {/* Time */}
                      <div className="w-[7.5rem] flex-shrink-0 text-right flex items-center justify-end h-9">
                        <span className="text-[#B5985A] font-medium text-sm whitespace-nowrap">
                          {event.time}
                        </span>
                      </div>

                      {/* Icon on the line */}
                      <div className="flex-shrink-0 w-9 relative z-10">
                        <div className="w-9 h-9 rounded-full bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center">
                          <event.icon className="w-5 h-5 text-[#B5985A]" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-2">
                        <h3 className="font-serif text-xl text-[#2D2A26] mb-1 flex items-center h-9">
                          {event.title}
                        </h3>

                        {event.subtitle && (
                          <p className="text-[#B5985A] text-sm italic">
                            {event.subtitle}
                          </p>
                        )}

                        {event.description && (
                          <p className="text-[#666] text-sm leading-relaxed">
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Shuttle icon — at the end of Saturday's timeline */}
                  {dayIndex === 1 && (
                    <div className="flex gap-6 relative">
                      <div className="w-[7.5rem] flex-shrink-0 h-9" />
                      <div className="flex-shrink-0 w-9 relative z-10">
                        <div className="w-9 h-9 rounded-full bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center">
                          <Bus className="w-5 h-5 text-[#666]" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Shuttle text — below Saturday's timeline */}
              {dayIndex === 1 && (
                <p className="text-[#666] leading-relaxed text-center mt-6 max-w-lg mx-auto">{t('scheduleShuttleNote')}</p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
